import React, { useCallback } from 'react';
import { Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useModalStore, useAuthStore, useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import AgentSelector from '@/components/Navigation/AgentSelector';
import AgentDropdown from '@/components/Dropdowns/AgentDropdown';
import useCopyAgentLink from '@/hooks/useCopyAgentLink';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const AgentChatRouteNavigationHeaderRight = React.memo(({ agentId }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const favoriteAgentsCount = useAuthStore(state => state.user?.favoriteAgentIds?.length || 0);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const toggleFavoriteAgent = useAuthStore(state => state.toggleFavoriteAgent);
  const favoriteAgentIds = useAuthStore(state => state.user?.favoriteAgentIds) || [];
  const isSaved = favoriteAgentIds.includes(agentId) || false;
  const { copyAgentLink } = useCopyAgentLink();

  if (!userId) {
    return (
      <NavigationBarButtonContainer rightPadding>
        <Button
          text={t('nav.routes.chat.logIn') || 'Log In'}
          variant="secondary"
          size={isXS ? 'small' : 'medium'}
          onPress={() => router.push('/login')}
          style={{ marginRight: isXS ? 4 : 6 }}
        />
        <Button
          text={t('nav.routes.chat.signUp') || 'Sign Up'}
          variant="primary"
          size={isXS ? 'small' : 'medium'}
          onPress={() => router.push('/signup')}
        />
      </NavigationBarButtonContainer>
    );
  }

  const handleToggleSave = useCallback(async () => {
    try {
      if (isGhost && favoriteAgentsCount >= 1) {
        openModal('GhostSignupModal');
        return;
      }
      if (!isPlus && favoriteAgentsCount >= 5) {
        openModal('PricingModal', {
          title: t('nav.routes.agentChat.saveFavoriteAgents'),
          subtitle: t('nav.routes.agentChat.saveFavoriteAgentsDesc')
        });
        return;
      }

      const success = await toggleFavoriteAgent(agentId);
      if (success) {
        addToast(isSaved ? t('nav.routes.agentChat.removedFromFavorites') : t('nav.routes.agentChat.addedToFavorites'), 'success');
      } else {
        addToast(isSaved ? t('nav.routes.agentChat.failedToUnsave') : t('nav.routes.agentChat.failedToSave'), 'error');
      }
    } catch (error) {
      if (error?.name === 'ApiError' && error?.message) {
        addToast(error.message, 'error', error?.data?.actions);
      } else {
        addToast(isSaved ? t('nav.routes.agentChat.failedToUnsave') : t('nav.routes.agentChat.failedToSave'), 'error');
      }
    }
  }, [toggleFavoriteAgent, agentId, isSaved, addToast, favoriteAgentsCount, isGhost, isPlus, openModal, t]);

  if (isGhost) return null;

  const TriggerComponent = React.memo(() => (
    <NavigationBarButton
      icon="ellipsis"
      tooltip={t('nav.routes.agentChat.moreOptions')}
      title={t('nav.routes.agentChat.moreOptions')}
    />
  ));

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon="link"
        title={t('dropdowns.copyLink')}
        tooltip={t('dropdowns.copyLink')}
        onPress={() => copyAgentLink(agentId)}
      />
      <NavigationBarButton
        icon="star"
        solidIcon={isSaved}
        title={isSaved ? t('nav.routes.agentChat.unsave') : t('common.save')}
        tooltip={isSaved ? t('nav.routes.agentChat.unsave') : t('common.save')}
        onPress={handleToggleSave}
      />
      <AgentDropdown 
        agentId={agentId} 
        isSaved={isSaved}
        TriggerComponent={TriggerComponent}
        onToggleSave={handleToggleSave}
      />
    </NavigationBarButtonContainer>
  );
});

AgentChatRouteNavigationHeaderRight.displayName = 'AgentChatRouteNavigationHeaderRight';

function AgentChatRouteNavigationHeaderLeft({ agent }) {
  const userId = useAuthStore(state => state.user?.id);
  const router = useRouter();

  if (!userId) {
    return (
      <DefaultRouteNavigationHeaderLeft>
        <Pressable
          onPress={() => router.push('/agents')}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4, cursor: 'pointer' }}
          accessibilityRole="link"
        >
          <Icon name="arrow-left" size={13} />
          <Text size="s" weight="bold">All Bots</Text>
        </Pressable>
      </DefaultRouteNavigationHeaderLeft>
    );
  }

  return (
    <DefaultRouteNavigationHeaderLeft>
      <AgentSelector agentId={agent?.id} />
    </DefaultRouteNavigationHeaderLeft>
  );
}

function getAgentChatRouteNavigationOptions(agent, isXS) {
  if (!agent?.id) return {};

  const title = "New Chat";

  return {
    title,
    headerTitle: title,
    headerLeft: () => <AgentChatRouteNavigationHeaderLeft agent={agent} />,
    headerRight: () => <AgentChatRouteNavigationHeaderRight agentId={agent.id} />
  };
}

export default getAgentChatRouteNavigationOptions; 